import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Typography,
  Paper,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SearchIcon from '@mui/icons-material/Search';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import InfoIcon from '@mui/icons-material/Info';
import ListIcon from '@mui/icons-material/List';

const ProjectsTutorial = () => {
  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  const steps = [
    {
      label: 'Create a Project',
      description: `Start by creating a project with a descriptive name (e.g., "Patio Table" or "Kitchen Remodel"). Enter your search query describing what you want to build.`,
      image: '/c_eling _epair_screenshot.png',
      tip: 'Be specific in your search query to get better results.'
    },
    {
      label: 'View Search Results',
      description: `Your search will return highly relevant video tutorials and product recommendations. Click on "Videos" or "Products" to expand each section.`,
      image: '/vid_prd_screen.png',
      tip: 'Videos are sorted by relevance to your project description.'
    },
    {
      label: 'Select Your Project',
      description: `After searching, select your project from the dropdown menu in the "Your Projects" section. This ensures that all subsequent actions apply to the correct project.`,
      image: '/select_project.png',
      tip: 'Your most recent search will appear at the top of the projects list.'
    },
    {
      label: 'Get Detailed Steps',
      description: `Click the "View Details" button and then click the "Add Details" button to get step-by-step instructions on how to complete your project. Each click generates the next set of detailed steps.`,
      image: '/add_details1.png',
      tip: 'You must click "View Details" after adding details to see the updated instructions.'
    },
    {
      label: 'Repeat for Complete Instructions',
      description: `Continue clicking "Add Details" followed by "View Details" until you have all the steps you need for your project. Each click reveals more detailed instructions.`,
      image: '/add_details2.png',
      tip: 'The more details you add, the more comprehensive your instructions will be.'
    },
    {
      label: 'Toggle Follow-Up Questions',
      description: `Click the "Ask Follow-Up Question" button to open the popup chat interface. This button appears when you have an active project selected.`,
      image: '/click_ask_question.png',
      tip: 'The button toggles between showing and hiding the follow-up question form, keeping your workspace clean until you need it.'
    },
    {
      label: 'Chat About Your Project',
      description: `Once the follow-up question form is open, ask specific questions about your project and receive expert advice tailored to your active project.`,
      image: '/toggle_question.png',
      tip: 'Ask clear, specific questions about materials, techniques, or modifications to get the most helpful responses.'
    }
  ];

  return (
    <>
      <IconButton
        onClick={handleOpen}
        aria-label="Help"
        color="primary"
        sx={{ 
          position: 'fixed',
          bottom: 20,
          right: 20,
          bgcolor: 'white',
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
          '&:hover': {
            bgcolor: '#f0f7ff',
          },
          zIndex: 1000,
          width: 60,
          height: 60
        }}
      >
        <HelpOutlineIcon fontSize="large" />
      </IconButton>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: '#1e3c72',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            How to Use the Video Search & Project Builder
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 4 }}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="body1" paragraph>
              Follow these steps to make the most of our video search and project builder features:
            </Typography>
          </Box>

          <Stepper activeStep={activeStep} orientation="vertical" sx={{ mb: 4 }}>
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {step.label}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" paragraph>
                      {step.description}
                    </Typography>
                    
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        p: 2, 
                        bgcolor: '#f0f7ff', 
                        borderRadius: 2,
                        mb: 2,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1
                      }}
                    >
                      <InfoIcon color="primary" sx={{ mt: 0.5 }} />
                      <Typography variant="body2" color="primary.dark">
                        <strong>Tip:</strong> {step.tip}
                      </Typography>
                    </Paper>
                    
                    <Paper
                      elevation={3}
                      sx={{
                        borderRadius: 2,
                        overflow: 'hidden',
                        mb: 2,
                        maxWidth: 500,
                        mx: 'auto'
                      }}
                    >
                      <Box
                        component="img"
                        src={step.image}
                        alt={`Step ${index + 1}: ${step.label}`}
                        sx={{
                          width: '100%',
                          height: 'auto'
                        }}
                      />
                    </Paper>
                  </Box>
                  <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                    <Button
                      disabled={index === 0}
                      onClick={handleBack}
                      variant="outlined"
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      onClick={index === steps.length - 1 ? handleClose : handleNext}
                      sx={{ 
                        background: index === steps.length - 1 
                          ? 'linear-gradient(90deg, #2a5298 0%, #1e3c72 100%)' 
                          : undefined 
                      }}
                    >
                      {index === steps.length - 1 ? 'Finish' : 'Continue'}
                    </Button>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>

          {activeStep === steps.length && (
            <Paper square elevation={0} sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                All steps completed! You're ready to start using the video search feature.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                <Button onClick={handleReset} variant="outlined">
                  Review Steps Again
                </Button>
                <Button onClick={handleClose} variant="contained">
                  Start Building
                </Button>
              </Box>
            </Paper>
          )}

          <Divider sx={{ my: 4 }} />

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1e3c72' }}>
              Quick Reference Guide
            </Typography>
            <Box 
              sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 3,
                mt: 2
              }}
            >
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 2, 
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <SearchIcon sx={{ fontSize: 40, color: '#1e3c72' }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  1. Search
                </Typography>
                <Typography variant="body2" textAlign="center">
                  Enter project name & description to find relevant videos & products
                </Typography>
              </Paper>
              
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 2, 
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <ListIcon sx={{ fontSize: 40, color: '#1e3c72' }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  2. Select Project
                </Typography>
                <Typography variant="body2" textAlign="center">
                  Choose your project from the dropdown menu to work with it
                </Typography>
              </Paper>
              
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 2, 
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <DesignServicesIcon sx={{ fontSize: 40, color: '#1e3c72' }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  3. Add Details
                </Typography>
                <Typography variant="body2" textAlign="center">
                  Click "Add Details" then "View Details" repeatedly to get complete instructions
                </Typography>
              </Paper>
              
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 2, 
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <SearchIcon sx={{ fontSize: 40, color: '#1e3c72' }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  4. Ask Questions
                </Typography>
                <Typography variant="body2" textAlign="center">
                  Toggle the follow-up question box to chat about your specific project
                </Typography>
              </Paper>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProjectsTutorial;